<template>
    <div
      class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50"
      v-if="showing"
    >
      <div
        class="bg-gray-900 text-blue-400 p-3 md:p-5 border-2 border-blue-400 rounded-lg w-11/12 max-w-3xl max-h-[90%] overflow-y-auto font-mono relative"
      >
        <!-- Error message display -->
        <div v-if="errorMessage" class="bg-red-500 text-white p-2 rounded mb-4">
          {{ errorMessage }}
        </div>

          <div class="flex flex-col items-center mb-4">
            <h2 class="text-xl md:text-2xl font-bold tracking-wider">Boat Timetable</h2>
            <div class="flex justify-between w-full text-base md:text-lg mt-2">
              <span>Harbor: {{ station.name }}</span>
              <span>Time: {{ formattedTime }}</span>
              <button
                v-if="!selectedBoat"
                @click="close"
                class="bg-gray-700 text-white py-1 px-3 rounded hover:bg-gray-600"
              >
                Close
              </button>
              <button
                v-else
                @click="back"
                class="bg-gray-700 text-white py-1 px-3 rounded hover:bg-gray-600"
              >
                Back
              </button>
            </div>
          </div>

          <!-- Search input -->
          <div v-if="!selectedBoat" class="relative">
            <input
              type="text"
              v-model="searchText"
              placeholder="Search for a destination..."
              class="w-full p-2 pr-10 mb-4 bg-gray-800 border border-green-500 rounded text-white"
            />
            <button
              v-if="searchText"
              @click="clearSearch"
              class="absolute right-2 top-2 text-gray-400 hover:text-white focus:outline-none"
              type="button"
            >
              ✕
            </button>
          </div>
    
          <div v-if="loading" class="text-center text-2xl">
            Loading timetable...
          </div>

          <div
            v-else-if="!selectedBoat && boats.length === 0"
            class="text-center text-xl text-yellow-400 p-4"
          >
            No boats available for this destination or time.
          </div>
    
          <!-- Main timetable view (list of boats) -->
          <div v-else-if="!selectedBoat">
            <table class="w-full border-collapse">
              <thead>
                <tr>
                  <th class="hidden md:table-cell border border-blue-400 p-2 text-center">Vessel</th>
                  <th class="border border-blue-400 p-2 text-center">Destination</th>
                  <th class="border border-blue-400 p-2 text-center">Depart</th>
                  <th class="border border-blue-400 p-2 text-center">Arrive</th>
                  <th class="border border-blue-400 p-2 text-center">Carrier</th>
                  <th class="border border-blue-400 p-2 text-center">Price</th>
                </tr>
              </thead>
              <tbody>
                <!-- Current day boats -->
                <tr
                  v-for="(boat, index) in currentDayBoats"
                  :key="'current-' + index"
                  @click="selectBoat(boat)"
                  class="cursor-pointer hover:bg-gray-700"
                >
                  <td class="hidden md:table-cell border border-blue-400 text-center">
                    <img v-if="boat.carrier_image" :src="boat.carrier_image" alt="Carrier" class="h-7 mx-auto" />
                    <span v-else>🚢</span>
                  </td>
                  <td class="border border-blue-400 p-2 text-center">
                    {{ boat.to_station_name }}
                  </td>
                  <td class="border border-blue-400 p-2 text-center">
                    {{ boat.depart }}
                  </td>
                  <td class="border border-blue-400 p-2 text-center">
                    {{ boat.arrive }}
                  </td>
                  <td class="border border-blue-400 p-2 text-center">
                    {{ boat.carrier_name }}
                  </td>
                  <td class="border border-blue-400 p-2 text-center">
                    {{ boat.currency_symbol }}{{ boat.price?.toFixed(2) }}
                  </td>
                </tr>
                <!-- Tomorrow header and boats -->
                <template v-if="nextDayBoats.length > 0">
                  <tr>
                    <td :colspan="6" class="border border-blue-400 p-3 text-center bg-blue-900 font-bold text-lg">
                      Tomorrow
                    </td>
                  </tr>
                  <tr
                    v-for="(boat, index) in nextDayBoats"
                    :key="'next-' + index"
                    @click="selectBoat(boat)"
                    class="cursor-pointer hover:bg-gray-700"
                  >
                    <td class="hidden md:table-cell border border-blue-400 text-center">
                      <img v-if="boat.carrier_image" :src="boat.carrier_image" alt="Carrier" class="h-7 mx-auto" />
                      <span v-else>🚢</span>
                    </td>
                    <td class="border border-blue-400 p-2 text-center">
                      {{ boat.to_station_name }}
                    </td>
                    <td class="border border-blue-400 p-2 text-center">
                      {{ boat.depart }}
                    </td>
                    <td class="border border-blue-400 p-2 text-center">
                      {{ boat.arrive }}
                    </td>
                    <td class="border border-blue-400 p-2 text-center">
                      {{ boat.carrier_name }}
                    </td>
                    <td class="border border-blue-400 p-2 text-center">
                      {{ boat.currency_symbol }}{{ boat.price?.toFixed(2) }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
    
          <!-- Purchase view with timeline: Displayed when a boat is selected -->
          <div v-else class="bg-gray-800 p-5 border border-blue-400 rounded mt-4 text-center">
            <h3 class="text-xl font-bold mb-3">Select Your destination</h3>
    
            <!-- Loading spinner for boat stops -->
            <div v-if="loadingStops" class="text-center mb-4 text-2xl">
              Loading stops...
            </div>
    
            <!-- Timeline view for boat stops -->
            <div v-else-if="boatStops.length > 0" class="flex flex-col space-y-2 overflow-y-auto mb-4">
              <div
                v-for="(stop, index) in boatStops"
                :key="index"
                @click="selectStop(stop)"
                :class="['cursor-pointer p-2 border rounded', selectedStop && selectedStop.station_id === stop.station_id ? 'bg-blue-500 text-white' : 'bg-gray-700 text-white']"
              >
                <p><strong>{{ stop.station_name }}</strong></p>
                <p>Arrive: {{ stop.arrive }}</p>
                <p>Price: {{ stop.currency_symbol }}{{ stop.price?.toFixed(2) }}</p>
              </div>
            </div>
    
            <!-- Confirm ticket button -->
            <Button
              class="bg-gray-700 text-white py-2 px-4 rounded hover:bg-gray-600 mt-4"
              :class="{'bg-green-500 hover:bg-green-600': selectedStop}"
              :disabled="!selectedStop"
              :loading="confirming"
              @click="confirmTicket"
            >
              Confirm Ticket
            </Button>
          </div>
      </div>
    </div>
  </template>
    
<script setup>
import { validRoute } from '@/utils/gameplay.js';
import { timeStringToSeconds } from '@/utils/time.js';
import { ref, computed, watch } from 'vue';
import Button from '@/components/Button.vue';

const props = defineProps({
  station: {
    type: Object,
    required: false,
  },
  time: {
    type: Number,
    required: true,
  },
  dayOfWeek: {
    type: Number,
    required: true,
  },
});

const BaseUrl = ref(baseUrl);
const showing = defineModel('modelValue', { default: false, type: Boolean });
const emit = defineEmits(['bought-ticket', 'close', 'wait-for-boat']);

const errorMessage = ref(null);

const confirming = ref(false);

const boats = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedBoat = ref(null);

// New state for boat stops timeline
const boatStops = ref([]);
const selectedStop = ref(null);
const loadingStops = ref(false);

const searchText = ref('');

const formattedTime = computed(() => {
  const hours = Math.floor(props.time / 3600) % 24;
  const minutes = Math.floor(props.time / 60) % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
});

// Separate boats into current day and next day
const currentDayBoats = computed(() => {
  return boats.value.filter(boat => {
    // If depart_day is not set, assume it's current day
    if (boat.depart_day === undefined || boat.depart_day === null) {
      return true;
    }
    // Compare as numbers to handle both string and number types
    return Number(boat.depart_day) === Number(props.dayOfWeek);
  });
});

const nextDayBoats = computed(() => {
  return boats.value.filter(boat => {
    // Only include boats that have depart_day set and it's different from current day
    if (boat.depart_day === undefined || boat.depart_day === null) {
      return false;
    }
    return Number(boat.depart_day) !== Number(props.dayOfWeek);
  });
});

function parseTime(hhmmss) {
  const parts = hhmmss.split(':').map(Number);
  const [h, m, s = 0] = parts;          // default s to 0 if missing
  return h * 3600 + m * 60 + s;
}

function isTimeAfter(timeA, timeB) {
  return parseTime(timeA) >= parseTime(timeB);
}

/*function getFormattedTime(time) {
  const hours = Math.floor(time / 3600) % 24;
  const minutes = Math.floor(time / 60) % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}*/

const stationName = computed(() => {
  if (boats.value.length > 0) {
    return boats.value[0].from_station_name;
  }
  return 'Unknown Harbor';
});

async function fetchBoats() {
  const timetableTime = props.time;
  const timetableDay = props.dayOfWeek;
  loading.value = true;
  try {
    let url = `${baseUrl}api/boats?from_id=${props.station.id}&time=${formattedTime.value}&day=${props.dayOfWeek}`;
    if (searchText.value.trim()) {
      url += `&q=${encodeURIComponent(searchText.value.trim())}`;
    }
    const response = await fetch(url);
    const data = await response.json();
    if (!response.ok || !Array.isArray(data.data)) throw new Error('Timetable unavailable');
    boats.value = data.data.map(service => ({ ...service, departure_local: Math.floor(timetableTime / 86400) * 86400 + ((Number(service.depart_day) - Number(timetableDay) + 7) % 7) * 86400 + timeStringToSeconds(service.depart) }));
  } catch (err) {
    error.value = 'Error fetching boat timetable';
    console.error(err);
  } finally {
    loading.value = false;
  }
}

// When a boat row is clicked, select the boat and immediately fetch its stops.
async function selectBoat(boat) {
  if(props.time >= boat.departure_local) {
    errorMessage.value = 'This boat has already departed.';
    setTimeout(() => {
      errorMessage.value = null;
    }, 5000);
    // If the boat has already departed, do not allow selection and refetch the list.
    await fetchBoats();
    return;
  }
  selectedBoat.value = boat;
  // Immediately fetch boat stops timeline.
  await fetchBoatStops(boat);
}

function cancelPurchase() {
  selectedBoat.value = null;
  boatStops.value = [];
  selectedStop.value = null;
}

function formatPrice(price) {
  return `$${price.toFixed(2)}`;
}

function close() {
  emit('close');
  selectedBoat.value = null;
  boats.value = [];
  boatStops.value = [];
  selectedStop.value = null;
}

function back() {
  selectedBoat.value = null;
  boatStops.value = [];
  selectedStop.value = null;
}

function exitStation() {
  close();
}

// Fetch boat stops for the selected boat using the API.
// The URL format is: api/boats/$boatId?from_id=$stationId
async function fetchBoatStops(boat) {
  loadingStops.value = true;
  try {
    const url = `${baseUrl}api/boats/${boat.id}?from_id=${props.station.id}&day=${boat.depart_day}&source=${boat.source || ''}`;
    const response = await fetch(url);
    const data = await response.json();
    // Assume the API returns an array of boat stop objects.
    if (!response.ok || !Array.isArray(data.data)) throw new Error('Stops unavailable');
    boatStops.value = data.data.filter(stop => String(stop.station_id) !== String(props.station.id));
    selectedStop.value = boatStops.value.at(-1) || null;
  } catch (err) {
    console.error("Error fetching boat stops:", err);
  } finally {
    loadingStops.value = false;
  }
}
  
function selectStop(stop) {
  selectedStop.value = stop;
}
  
async function confirmTicket() {
  if (confirming.value || !selectedBoat.value || !selectedStop.value) return;
  confirming.value = true;

  if(props.time >= selectedBoat.value.departure_local) {
    selectedBoat.value = null;
    selectedStop.value = null;
    errorMessage.value = 'This boat has already departed.';
    setTimeout(() => {
      errorMessage.value = null;
    }, 5000);
    confirming.value = false;
    // If the boat has already departed, do not allow selection and refetch the list.
    await fetchBoats();
    return;
  }
  
  try {
    const url = `${baseUrl}api/boats/${selectedBoat.value.id}/route?from_id=${props.station.id}&to_id=${selectedStop.value.station_id}&source=${selectedBoat.value.source || ''}`;
    const response = await fetch(url);
    const routeData = await response.json();
    if (!response.ok || !validRoute(routeData.data)) throw new Error('Route unavailable');
    if (props.time >= selectedBoat.value.departure_local) throw new Error('Departure has passed');

    const num_days_delay = Math.floor(selectedBoat.value.departure_local / 86400) - Math.floor(props.time / 86400);
    
    const transport = { ...selectedBoat.value, ...selectedStop.value, id: selectedBoat.value.id, depart: selectedBoat.value.depart, to_id: selectedStop.value.station_id, to_station_name: selectedStop.value.station_name, depart_delay_days: num_days_delay };
    const end = boatStops.value.indexOf(selectedStop.value);
    emit('bought-ticket', {station: props.station, transport, route: routeData.data, stops: boatStops.value.slice(0, end + 1)});

    confirming.value = false;
    selectedBoat.value = null;
    boatStops.value = [];
    selectedStop.value = null;
  } catch (err) {
    console.error("Error fetching boat route:", err);
    errorMessage.value = 'Could not load this journey. Refresh the timetable and try again. No money was charged.';
  } finally {
    confirming.value = false;
  }
}

// Debounce functionality for search
let searchTimeout = null;

function debounceSearch() {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = setTimeout(() => {
    if (showing.value && !selectedBoat.value) {
      fetchBoats();
    }
  }, 500); // 500ms delay
}

// Watch for search text changes
watch(searchText, () => {
  debounceSearch();
});

function clearSearch() {
  searchText.value = '';
}

watch(showing, (newVal) => {
  if (newVal) {
    // Reset state when showing the component
    selectedBoat.value = null;
    boats.value = [];
    boatStops.value = [];
    selectedStop.value = null;
    searchText.value = '';
    fetchBoats();
  }
});

</script>
